/**
 * Config Validation - fail fast when generated config is missing or malformed.
 *
 * `js/constants.js` is GENERATED (npm run build) from js/data/*. If a build is
 * stale, partial, or a data export gets renamed, the old habit of reading
 * balance values through `config.x?.y || <inline default>` silently substitutes
 * a different game: marks, XP and credit curves quietly change and nothing
 * reports it. Those inline fallbacks also rot — they were the ORIGINAL tuning
 * values and drifted years out of date while reading like documentation.
 *
 * So: balance reads are now direct (config.progressionScaling.commandMarks.x),
 * and this module is the one place that checks the config is actually there.
 * Called once at boot, before any game state loads.
 *
 * Pure - no browser or gameState dependency, so the simulator and tests can
 * assert the same contract.
 */

/**
 * Required config paths, as [dotted path, kind].
 *
 * kind:
 *   'object'  - must be a non-null object with at least one own key
 *   'number'  - must be a finite number
 *   'divisor' - finite number, and not 0 (it is used as a denominator)
 *
 * Only values the code READS belong here. Adding a path makes it a hard boot
 * requirement, so add when code starts depending on it, not speculatively.
 */
export const REQUIRED_CONFIG = [
  // Core game data
  ['raidTierData', 'object'],
  ['raidRegions', 'object'],
  ['commonAreas', 'object'],
  ['factionSkills', 'object'],
  ['itemRarities', 'object'],
  ['commandUpgrades', 'object'],

  // Balance SSOT - js/data/progressionScaling.js
  ['progressionScaling.recipeInputs', 'object'],
  ['progressionScaling.raidXpTierMultipliers', 'object'],
  ['progressionScaling.skillEventDamage', 'object'],
  ['progressionScaling.bonusEventCascadeMultipliers', 'object'],

  ['progressionScaling.skillPointsPerLevel.base', 'number'],
  ['progressionScaling.skillPointsPerLevel.levelDivisor', 'divisor'],
  ['progressionScaling.skillPointsPerLevel.increment', 'number'],

  ['progressionScaling.synergyUnlocks.minLevel', 'number'],
  ['progressionScaling.synergyUnlocks.cost', 'number'],

  ['progressionScaling.commandMarks.perTechGear', 'number'],
  ['progressionScaling.commandMarks.perArtifactUnlock', 'number'],
  ['progressionScaling.commandMarks.perArtifactCraft', 'number'],
  ['progressionScaling.commandMarks.t3WinsDivisor', 'divisor'],
  ['progressionScaling.commandMarks.perLevel', 'number'],
  ['progressionScaling.commandMarks.unlockThreshold', 'number'],

  ['progressionScaling.promotionCreditLevy.keepFraction', 'number'],
  ['progressionScaling.promotionCreditLevy.floor', 'number'],

  ['progressionScaling.creditsScaling.globalMultiplier', 'number'],
  ['progressionScaling.creditsScaling.tierMultipliers', 'object'],

  ['progressionScaling.craftingCostScaling.globalMultiplier', 'number'],
  ['progressionScaling.craftingCostScaling.rarityMultipliers', 'object'],
  ['progressionScaling.craftingDiscountPerTour', 'number'],

  ['progressionScaling.energyPurchaseScaling.escalationFactor', 'number'],

  // Here for exactly the reason in this file's header: it was read through
  // `?? 0`, and that fallback fails silently in the worst possible way — a stale
  // build ships a game where no key ever drops and the Archive is permanently
  // empty, which looks identical to "the player has not found one yet".
  //
  // `collapseKeys` itself is deliberately NOT listed. The item arrays it sits
  // beside (itemsCommon, itemsStoryRelics, …) are not validated here either;
  // data-completeness is a build-time question that collapseKeys.test.js
  // already answers, where this file's job is the balance values whose absence
  // is invisible at runtime.
  ['progressionScaling.collapseKeyDropChance', 'number'],
];

function readPath(root, path) {
  return path.split('.').reduce((node, key) => (node == null ? undefined : node[key]), root);
}

function describeProblem(path, kind, value) {
  if (value === undefined) return `${path} is missing`;
  if (kind === 'object') {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return `${path} should be an object, got ${Array.isArray(value) ? 'array' : typeof value}`;
    }
    if (Object.keys(value).length === 0) return `${path} is an empty object`;
    return null;
  }
  if (!Number.isFinite(value)) return `${path} should be a finite number, got ${JSON.stringify(value)}`;
  if (kind === 'divisor' && value === 0) return `${path} is 0, but it is used as a divisor`;
  return null;
}

/**
 * Check a config module against REQUIRED_CONFIG.
 * @param {Object} config - the imported js/constants.js namespace
 * @returns {string[]} human-readable problems; empty means valid
 */
export function validateConfig(config) {
  if (config == null || typeof config !== 'object') return ['config module did not load at all'];
  const problems = [];
  for (const [path, kind] of REQUIRED_CONFIG) {
    const problem = describeProblem(path, kind, readPath(config, path));
    if (problem) problems.push(problem);
  }
  return problems;
}

/**
 * Throw unless the config is complete. Call once at boot, before loading a save
 * or reading any balance value.
 * @param {Object} config - the imported js/constants.js namespace
 * @throws {Error} listing every problem found
 */
export function assertConfigLoaded(config) {
  const problems = validateConfig(config);
  if (problems.length === 0) return;
  throw new Error(
    `Game config failed to load (${problems.length} problem${problems.length === 1 ? '' : 's'}). ` +
    `js/constants.js is generated — run \`npm run build\`.\n  - ${problems.join('\n  - ')}`
  );
}
